import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin } from "lucide-react";
import { COMPANY } from "../constants/company";
import { Button } from "../components/ui/button";

const sections = [
  { title: "Information We Collect", text: "We collect personal information you provide through our intake forms — including your name, contact details, filing status, dependent information, income details, and electronic signature — as well as documents you upload such as W-2s, 1099s, and identification. This information is used solely to prepare and file your tax returns and to provide related services." },
  { title: "How We Use Your Information", text: "Your information is used to prepare tax filings, communicate with you about your account and appointments, and meet legal and regulatory obligations. We do not sell, rent, or share your personal information with third parties for marketing purposes." },
  { title: "Data Security", text: "Documents uploaded through our portal are stored in encrypted cloud storage. Access to client data is restricted to authorized staff of Laronta Pro Tax Services LLC. Authentication sessions use secure, httpOnly cookies." },
  { title: "Data Retention", text: "We retain tax records and supporting documents for the period required by IRS regulations and applicable state law. You may request deletion of your account data, subject to these legal retention requirements." },
  { title: "Your Rights", text: "You may request access to, correction of, or deletion of your personal information by contacting our office. We will respond to verified requests within a reasonable timeframe." },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo-link" className="flex items-center">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-10 w-auto rounded-sm" />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/" data-testid="privacy-back-link"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Link>
          </Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tighter">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: June 2026 · {COMPANY.name}</p>
        <div className="mt-10 space-y-8">
          {sections.map((s, i) => (
            <section key={s.title} data-testid={`privacy-section-${i}`}>
              <h2 className="text-xl font-heading font-semibold">{i + 1}. {s.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </section>
          ))}
          <section data-testid="privacy-contact-section" className="bg-muted/50 border border-border rounded-md p-8">
            <h2 className="text-xl font-heading font-semibold">6. Contact Us</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              For privacy questions or requests regarding your data, contact us:
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {COMPANY.name}, {COMPANY.fullAddress}</p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary" />
                <a href={COMPANY.phoneHref} data-testid="privacy-phone-link" className="hover:text-primary transition-colors">{COMPANY.phone}</a>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                <a href={COMPANY.emailHref} data-testid="privacy-email-link" className="hover:text-primary transition-colors">{COMPANY.email}</a>
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
